import { Dispatch } from 'redux'
import firebase from 'firebase/app'

import { CourseActionsTypes } from '@store/lessonPreview/types'
import { setCourseLessonAction, setLoadingCourseLessonAction } from '@store/lessonPreview/actions'
import {
  activeCoursesRef,
  studentsRef,
  firestoreRef,
  publishedCoursesRef,
  courseRef,
} from '@configs/firebase'
import { HomeworkStatuses, IEssayContent, ILessonHomework, IStudentProfile } from '../models'
import axios from 'axios'
import { CLASSROOM_BLOCK_TYPES, CORRECTING_PAGE_NAME, FIREBASE_CONFIG } from '@utils/constants'
import { logger } from '@utils/ConsoleLogger'
import { generateDigitsId } from '@utils/helperst'
import { IErrorsState } from '@components/LessonWidget/components/CorrectionErrorModal'

const previewPagePaths: string[] = [
  '/teacher/course/:courseId/lesson/:lessonId/page/:pageId',
  '/student/course/:courseId/lesson/:lessonId/page/:pageId',
]

export const getCourseLesson = ({
  courseId,
  lessonId,
  studentId,
  preventLoading,
  path,
}: {
  courseId: string
  lessonId: string
  studentId?: string
  preventLoading?: boolean
  path: string
}) => {
  return async (dispatch: Dispatch<CourseActionsTypes>) => {
    if (!preventLoading) dispatch(setLoadingCourseLessonAction(true))
    try {
      let currentLessonId, lessonIds

      if (studentId) {
        const courseSnap = await studentsRef.doc(`${studentId}/courses/${courseId}`).get()
        const data = courseSnap.data() || {}
        currentLessonId = data?.currentLessonId
        lessonIds = (data.lessonIds || []).concat(data.showLessonIds || [])
      }

      if (studentId) {
        const lessonSnap = await studentsRef
          .doc(`${studentId}/courses/${courseId}/lessons/${lessonId}`)
          .get()
        if (lessonSnap.exists) {
          const data = lessonSnap.data() as Omit<ILessonHomework, 'id'>
          dispatch(
            setCourseLessonAction({
              ...data,
              id: lessonId,
              isAllowed: lessonIds?.includes(lessonId),
              isCurrent: currentLessonId === lessonId,
            })
          )
          return
        }
      }
      const getCourseRef = previewPagePaths.includes(path) ? courseRef : activeCoursesRef
      const lesson = await getCourseRef
        .doc(courseId)
        .collection('lessons')
        .doc(lessonId)
        .get()
      const data = (await lesson.data()) as Omit<ILessonHomework, 'id'>

      const homework = await studentsRef
        .doc(studentId)
        .collection('homeworks')
        .doc(lessonId)
        .get()

      if (homework.exists) {
        const res = await Promise.all(
          data?.pages?.map(async currentPage => {
            const currenPageState = await Promise.all(
              currentPage?.pageState.map(async state => {
                if (state.type === 'essay') {
                  const content = state?.content as IEssayContent

                  const randomWord =
                    (await homework
                      .data()
                      .pages.find(page => page.id === currentPage.id)
                      ?.pageState?.find(state => state.type === 'essay')?.content?.randomWord) || []

                  return {
                    ...state,
                    content: {
                      ...content,
                      randomWord: [...randomWord],
                    },
                  }
                } else return state
              })
            )
            return {
              ...currentPage,
              pageState: [...currenPageState],
            }
          }) ?? []
        )
        dispatch(
          setCourseLessonAction({
            ...data,
            pages: [...res],
            id: lessonId,
            isAllowed: lessonIds?.includes(lessonId),
          })
        )
      } else {
        dispatch(
          setCourseLessonAction({
            ...data,
            id: lessonId,
            isAllowed: lessonIds?.includes(lessonId),
          })
        )
      }
    } finally {
      dispatch(setLoadingCourseLessonAction(false))
    }
  }
}

export const assignCourseForStudent = async ({ studentId, courseId }): Promise<void> => {
  const batch = firestoreRef.batch()
  batch.update(studentsRef.doc(studentId), { currentCourseId: courseId })
  const courseSnap = await studentsRef
    .doc(studentId)
    .collection('courses')
    .doc(courseId)
    .get()
  // @ts-ignore
  batch[courseSnap.exists ? 'update' : 'set'](
    studentsRef
      .doc(studentId)
      .collection('courses')
      .doc(courseId),
    {}
  )
  batch.update(publishedCoursesRef.doc(courseId), {
    students: firebase.firestore.FieldValue.arrayUnion(studentId),
  })

  await batch.commit()
}

interface IMakeCorrectionParams {
  mistakes: IErrorsState[]
}

export const createMakeErrorCorrectionPage = (
  params: IMakeCorrectionParams,
  student: IStudentProfile,
  homeworkId: string,
  pageId: number,
  forceHomeworkUpdate: (pageId: number) => void
) => {
  return async (dispatch: Dispatch<CourseActionsTypes>) => {
    try {
      const requests = []

      params.mistakes.forEach(mistake => {
        let typeOfExercise = Math.floor(Math.random() * 4) + 1
        if (mistake.context.includes('/')) {
          typeOfExercise = 4
        } else if (/\S+\s+\S+/.test(mistake.error)) {
          typeOfExercise = 4
        }
        requests.push(errorCorrectionPage.getExercises(mistake, student, typeOfExercise))
      })

      requests.push(errorCorrectionPage.getVocabulary(params.mistakes, student))

      const blocks = await Promise.all(requests)

      const homeworkSnap = await studentsRef
        .doc(student.id)
        .collection('homeworks')
        .doc(homeworkId)
        .get()

      if (!homeworkSnap.exists) {
        return
      }

      const homework = homeworkSnap.data()

      const updatedHomework = {
        ...homework,
        pages: [
          ...homework.pages,
          {
            name: CORRECTING_PAGE_NAME,
            pageState: blocks,
            status: HomeworkStatuses.NOT_READY,
            id: pageId,
          },
        ],
        isHomework: homework.isHomework || false,
        name: homework.name,
        id: homework.id,
        description: homework.description,
        image: homework.image || null,
        timestamp: homework.timestamp,
      }

      await studentsRef
        .doc(student.id)
        .collection('homeworks')
        .doc(homeworkId)
        .update(updatedHomework)

      dispatch(setCourseLessonAction(updatedHomework))

      await forceHomeworkUpdate(pageId)
    } catch (error) {
      logger.error(error)
      throw error
    }
  }
}

export const errorCorrectionPage = {
  getVocabulary: async (errors: IErrorsState[], student: IStudentProfile) => {
    const mistakes = errors.map(item => item.correct)
    const prompt = `Переведи слова или фразы ${mistakes.join('...')} на ${
      student.lang
    } язык. Ответ в формате: слово/фраза на испанском = перевод на ${
      student.lang
    } язык. Обязательно все пары разделены $ . Не ставь нумерацию, не добавляй ничего самостоятельно. Твой ответ должен быть строго в формате который я указал. Пример ответа 'hola = привет $ bailo = я танцую .....'`

    const result = await axios.post(FIREBASE_CONFIG.makeErrorCorrection, {
      studentId: student.id,
      level: student.level,
      interests: student.interests,
      prompt,
    })

    const data = result.data
    const words = []
    const generatedWords = data.split('$')
    const wordPattern = /^[^\s=]+(?:\s?[^\s=]+)* = [^\s=]+(?:\s?[^\s=]+)*$/

    generatedWords.forEach((word, i) => {
      const splittedWord = word.split('=')

      if (splittedWord.length === 2) {
        const original = splittedWord[0]?.trim() || ''
        const translation = splittedWord[1]?.trim() || ''

        if (wordPattern.test(`${original} = ${translation}`)) {
          const newWord = {
            id: i,
            original,
            translation,
          }
          words.push(newWord)
        }
      }
    })

    return { type: CLASSROOM_BLOCK_TYPES.VOCABULARY, content: { words } }
  },
  getExercises: async (mistake: IErrorsState, student: IStudentProfile, variant: number = 1) => {
    let prompt
    let exercizeSettings = {}
    switch (variant) {
      case 1:
        exercizeSettings = {
          variant,
          condition: 'Переместите слова/выражения в пропуск',
        }
        break
      case 2:
        exercizeSettings = {
          variant,
          condition: 'Вставьте слова в текст',
        }
        break
      case 3:
        exercizeSettings = {
          variant: 2,
          condition: 'Вставьте слова в текст',
        }
        break
      case 4:
        exercizeSettings = {
          variant,
          condition: 'Составьте предложение из слов',
        }
        break
    }

    if (variant === 4) {
      prompt = `Crea 1 oración de 5-10 palabras. Debe contener necesariamente la palabra - ${mistake.correct}. Simplemente especifique cada palabra con / sin espacios. Ejemplo: una oración / que / necesita / convertir. Solo haz una oferta, no agregues nada tú mismo.`
    } else {
      prompt = `Haz el ejercicio. El ejercicio consiste en omitir palabras en el texto. Las palabras [] figuran entre corchetes. Es necesario inventar palabras que puedan causar confusión. ¡Pero otros son necesariamente otros!!! Usa la palabra [${mistake.correct}] una vez.  En el resto [] utiliza otras formas de palabras / palabras similares. Número de palabras en [] 4-6 . No pongas numeración, no agregues nada tú mismo. ¡No debe haber corchetes vacíos!`
    }
    const result = await axios.post(FIREBASE_CONFIG.makeErrorCorrection, {
      studentId: student.id,
      prompt,
    })
    let text = result.data

    return variant === 4
      ? {
          type: CLASSROOM_BLOCK_TYPES.EXERCISE,
          content: { ...exercizeSettings, sentences: [{ id: generateDigitsId(), text }] },
          id: generateDigitsId(),
        }
      : {
          type: CLASSROOM_BLOCK_TYPES.EXERCISE,
          content: { ...exercizeSettings, text, id: generateDigitsId() },
          id: generateDigitsId(),
        }
  },
}
